/*
  useTranscriptSuggestions — state + actions for the transcript-based
  suggestion review workflow inside AssessmentEditor.

  Extracted from AssessmentEditor.jsx (originally ~2000 lines) so the
  editor page can focus on rendering the form itself. Every behaviour
  and testid is preserved verbatim from the pre-refactor version.

  Usage:
    const tx = useTranscriptSuggestions({
      assessmentId,
      readOnly,
      refreshAssessment: async () => { ... },
    });
    <TranscriptReviewPane {...tx} readOnly={readOnly} />
    tx.suggestionsByField.get(field.key)   // field-side pill lookup
    tx.pendingBySectionKey.get(sectionKey) // section-header badge
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function useTranscriptSuggestions({
  assessmentId,
  readOnly = false,
  refreshAssessment,
} = {}) {
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [speakerFilter, setSpeakerFilter] = useState("");
  const [experiencerFilter, setExperiencerFilter] = useState("");
  const [comparisonSuggestionId, setComparisonSuggestionId] = useState(null);
  const [reportSuggestion, setReportSuggestion] = useState(null);
  const [evidenceSuggestion, setEvidenceSuggestion] = useState(null);

  const refreshSuggestions = useCallback(async () => {
    if (!assessmentId) return;
    try {
      const r = await api.get(`/assessments/${assessmentId}/transcript-suggestions`,
                              { params: { status: "proposed" } });
      setSuggestions(r.data?.suggestions || []);
    } catch { /* ignore — panel just hides */ }
  }, [assessmentId]);

  useEffect(() => { refreshSuggestions(); }, [refreshSuggestions]);

  const generateSuggestions = useCallback(async () => {
    if (!assessmentId) return;
    setSuggestionsLoading(true);
    try {
      await api.post(`/assessments/${assessmentId}/suggest-from-transcript`);
      await refreshSuggestions();
      toast.success("Transcript suggestions ready", {
        description: "Review each item — nothing is written until you Accept.",
      });
    } catch (e) {
      toast.error("Suggestions unavailable", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setSuggestionsLoading(false); }
  }, [assessmentId, refreshSuggestions]);

  const acceptSuggestion = useCallback(async (s, opts = {}) => {
    try {
      await api.post(`/transcript-suggestions/${s.id}/accept`,
                     { overwrite: !!opts.overwrite });
      await Promise.all([
        refreshAssessment?.(),
        refreshSuggestions(),
      ]);
      toast.success(`Applied: ${s.field_label || s.field_key}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error === "existing_value") {
        setComparisonSuggestionId(s.id);
        return;
      }
      toast.error("Could not apply suggestion", {
        description: typeof detail === "string" ? detail : (detail?.message || e.message),
      });
    }
  }, [refreshAssessment, refreshSuggestions]);

  const rejectSuggestion = useCallback(async (s) => {
    try {
      await api.post(`/transcript-suggestions/${s.id}/reject`, {});
      await refreshSuggestions();
    } catch (e) {
      toast.error("Could not reject", { description: e?.response?.data?.detail || e.message });
    }
  }, [refreshSuggestions]);

  const deferSuggestion = useCallback(async (s) => {
    try {
      await api.post(`/transcript-suggestions/${s.id}/defer`, {});
      await refreshSuggestions();
    } catch (e) {
      toast.error("Could not defer", { description: e?.response?.data?.detail || e.message });
    }
  }, [refreshSuggestions]);

  const clarifySuggestion = useCallback(async (s, patch) => {
    try {
      await api.post(`/transcript-suggestions/${s.id}/clarify`, patch);
      toast.success("Clarified");
      await refreshSuggestions();
    } catch (e) {
      toast.error("Could not clarify", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }, [refreshSuggestions]);

  const batchAcceptSafe = useCallback(async () => {
    if (!assessmentId) return;
    try {
      const preview = await api.get(
        `/assessments/${assessmentId}/transcript-suggestions/batch-preview`,
      );
      const count = preview.data?.count || 0;
      if (count === 0) {
        toast.info("Nothing to batch-accept", {
          description: "Only Class A patient-reported items with no conflicts qualify.",
        });
        return;
      }
      const sample = (preview.data?.sample || [])
        .map((r) => `· ${r.field_label}`)
        .join("\n");
      if (!window.confirm(
        `Batch-accept ${count} Class A suggestion${count === 1 ? "" : "s"}?\n\n${sample}\n\n` +
        `Each row keeps its own audit entry. Safety-critical / measurement / diagnosis rows are excluded.`,
      )) return;
      const r = await api.post(
        `/assessments/${assessmentId}/transcript-suggestions/batch-accept`, {},
      );
      await Promise.all([refreshAssessment?.(), refreshSuggestions()]);
      toast.success(`Applied ${r.data?.count || 0} suggestions`);
    } catch (e) {
      toast.error("Batch accept failed", { description: e?.response?.data?.detail || e.message });
    }
  }, [assessmentId, refreshAssessment, refreshSuggestions]);

  // ---- Derived state --------------------------------------------------------

  // Fast lookup: field_key → suggestion row (spec §6 field-side pill).
  const suggestionsByField = useMemo(() => {
    const m = new Map();
    for (const s of suggestions || []) {
      // A field can technically have >1 suggestion — keep the newest.
      m.set(s.field_key, s);
    }
    return m;
  }, [suggestions]);

  // section_key → pending suggestion count (spec §6 review queue).
  const pendingBySectionKey = useMemo(() => {
    const m = new Map();
    for (const s of suggestions || []) {
      if (!s.section_key) continue;
      const status = s.review?.status || "proposed";
      if (status !== "proposed") continue;
      m.set(s.section_key, (m.get(s.section_key) || 0) + 1);
    }
    return m;
  }, [suggestions]);

  // Filtered view for the review pane (spec §6 keyboard efficiency).
  const filteredSuggestions = useMemo(() => {
    return (suggestions || []).filter((s) => {
      if (speakerFilter) {
        if ((s.assertion?.speaker || "unknown") !== speakerFilter) return false;
      }
      if (experiencerFilter) {
        if ((s.assertion?.experiencer || "unknown") !== experiencerFilter) return false;
      }
      return true;
    });
  }, [suggestions, speakerFilter, experiencerFilter]);

  return {
    // state
    suggestions,
    suggestionsLoading,
    speakerFilter,
    experiencerFilter,
    comparisonSuggestionId,
    reportSuggestion,
    evidenceSuggestion,
    readOnly,
    // derived
    suggestionsByField,
    pendingBySectionKey,
    filteredSuggestions,
    // actions
    generateSuggestions,
    refreshSuggestions,
    acceptSuggestion,
    rejectSuggestion,
    deferSuggestion,
    clarifySuggestion,
    batchAcceptSafe,
    // setters
    setSpeakerFilter,
    setExperiencerFilter,
    setComparisonSuggestionId,
    setReportSuggestion,
    setEvidenceSuggestion,
  };
}
